import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GetItemByID } from "../../api/requests/Api";
import { Item as ItemType } from "../../entity/Entity";
import "./Item.css";
import {addToCart} from "../../utils/CartStorage";

const Item = () => {
    const [item, setItem] = useState<ItemType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchParams] = useSearchParams();

    const [selectedImage, setSelectedImage] = useState<number>(0);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    const itemId = searchParams.get("id");

    useEffect(() => {
        if (itemId) {
            fetchItem(itemId);
        }
    }, [itemId]);

    const fetchItem = async (id: string) => {
        try {
            setLoading(true);
            const data = await GetItemByID(id);
            setItem(data[0]);
            setSelectedOptions(data[0]?.available_materials?.map(() => "") || []);
        } catch {
            setError("Failed to load item");
        } finally {
            setLoading(false);
        }
    };

    const handleOptionChange = (index: number, value: string) => {
        const updated = [...selectedOptions];
        updated[index] = value;
        setSelectedOptions(updated);
    };

    const allOptionsSelected = item?.available_materials
        ? selectedOptions.every((opt) => opt && opt.trim() !== "")
        : true;

    const resolveDynamicPrice = (): number | null => {
        if (!item?.dynamic_prices) return null;

        const found = item.dynamic_prices.find(
            (p) =>
                JSON.stringify(p.materials) === JSON.stringify(selectedOptions)
        );
        console.log(JSON.stringify(item.dynamic_prices), JSON.stringify(selectedOptions))

        return found?.price ?? null;
    };


    const handleAddToCart = () => {
        if (!item) return;

        if (!allOptionsSelected) {
            alert("Пожалуйста, выберите все опции");
            return;
        }

        const cartItem = {
            item_id: item._id,
            changeable_characteristics: selectedOptions,
            quantity: 1,
        };

        addToCart(cartItem);
        alert("Товар добавлен в корзину");
    };


    if (loading) return <div className="item-loading">Загрузка...</div>;
    if (error) return <div className="item-error">{error}</div>;
    if (!item) return null;

    return (
        <div className="item-page">
            <div className="item-top">
                {/* Left: Image / Gallery */}
                <div className="item-gallery">
                    <div className="item-image-wrapper">
                        <img
                            src={item.images_link[selectedImage]}
                            alt={item.title}
                            className="item-main-image"
                        />

                        {item.images_link.length > 1 && (
                            <>
                                <button
                                    className="gallery-btn left"
                                    onClick={() =>
                                        setSelectedImage((prev) =>
                                            prev > 0 ? prev - 1 : item.images_link.length - 1
                                        )
                                    }
                                >
                                    ‹
                                </button>

                                <button
                                    className="gallery-btn right"
                                    onClick={() =>
                                        setSelectedImage((prev) =>
                                            prev < item.images_link.length - 1 ? prev + 1 : 0
                                        )
                                    }
                                >
                                    ›
                                </button>

                                {/* ✅ THUMBNAILS */}
                                <div className="item-thumbnails">
                                    {item.images_link.map((img, idx) => (
                                        <img
                                            key={idx}
                                            src={img}
                                            alt={`preview-${idx}`}
                                            className={`item-thumbnail ${
                                                selectedImage === idx ? "active" : ""
                                            }`}
                                            onClick={() => setSelectedImage(idx)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Right: Info */}
                <div className="item-info">
                    <h1 className="item-title">{item.title}</h1>

                    {item.is_to_order ? (
                            <div className="item-to-order">
                            <span className="order-badge badge-to-order">
                                Изготовление на заказ
                                <span className="order-info to-order">i</span>
                            </span>
                            </div>
                        ) :
                        (
                            <div className="item-to-order">
                            <span className="order-badge badge-available">
                                В наличии
                                <span className="order-info available">i</span>
                            </span>
                            </div>
                        )
                    }
                    <div className="item-description" dangerouslySetInnerHTML={{__html: item.text_description}}/>

                    {/* Dynamic Selectors */}
                    {item.available_materials?.length > 0 &&
                        <div>
                            Для заказа необходимо выбрать вариант(ы):
                            <p></p>
                        </div>}

                    {item.available_materials?.map((options, idx) => (
                        <div key={idx} className="item-select">
                            <label>Опция {idx + 1}</label>
                            <select
                                value={selectedOptions[idx] || ""}
                                onChange={(e) =>
                                    handleOptionChange(idx, e.target.value)
                                }
                            >
                                <option value="" disabled>
                                    Choose...
                                </option>
                                {options.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}

                    <div className="item-price">
                        {item.is_dynamic_price
                            ? `${resolveDynamicPrice() ?? `от ${formatPrice(item.price)}`} ₽`
                            : `${formatPrice(item.price)} ₽`}
                    </div>


                    <button
                        className="item-add-btn"
                        onClick={handleAddToCart}
                        disabled={!allOptionsSelected}
                    >
                        Добавить в корзину
                    </button>

                </div>
            </div>

            {/* Characteristics */}
            <div className="item-characteristics">
                <h2>Характеристики</h2>
                <div className="characteristics-table">
                    {item.characteristics.map((char, index) => {
                        const [key, value] = char.split(":");
                        return (
                            <div key={index} className="characteristics-row">
                                <div className="char-key">{key}</div>
                                <div className="char-value">{value}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const formatPrice = (value: number | undefined) => {
    if (!value) return "";
    return value.toLocaleString("ru-RU").replace(/,/g, " ");
};


export default Item;
